<template>
  <div class="dialog">
    <div :class="surfaceClass">
      <div class="title">
        <slot name="title">
          <div/>
        </slot>
        <CloseIcon v-if="!persistent" class="btn-close" @click="close" />
      </div>
      <div :class="contentClass">
        <slot name="text"/>
      </div>
    </div>
    <div ref="scrim" class="scrim" @click="!persistent && close()"/>
  </div>
</template>

<script>
import CloseIcon from '@/assets/close.svg'
import { enableScroll, disableScroll } from '@/utils/enable-disable-scroll'
export default {
  name: 't-dialog',
  props: {
    fullscreen: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    persistent: {
      type: Boolean,
      default: false
    },
    light: {
      type: Boolean,
      default: false
    },
    beforeClose: {
      type: Function,
      default: () => {}
    }
  },
  data () {
    return {
      closeESC: this.exitWithESC
    }
  },
  computed: {
    surfaceClass () {
      return `surface ${this.fullscreen ? 'fullscreen' : ''}`
    },
    contentClass () {
      return `content ${this.dense ? 'dense' : ''}`
    }
  },
  components: { CloseIcon },
  mounted () {
    document.documentElement.appendChild(this.$el)
  },
  beforeDestroy () {
    document.documentElement.removeChild(this.$el)
  },
  methods: {
    open () {
      disableScroll([document.documentElement, this.$refs['scrim']])
      this.$el.classList.add('dialog--open')
      window.addEventListener('keyup', this.exitWithESC, true)
    },
    close () {
      this.$el.classList.remove('dialog--open')
      setTimeout(() => {
        enableScroll([document.documentElement, this.$refs['scrim']])
      }, 200)
      this.$emit('close')
      this.beforeClose()
      window.removeEventListener('keyup', this.exitWithESC, true)
    },
    exitWithESC (event) {
      if (event.key === 'Escape') {
        this.close()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 24;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  .surface {
    overflow: hidden;
    position: relative;
    backdrop-filter: blur(2px);
    max-width: 100%;
    max-height: 100%;
    background: var(--surface);
    color: var(--on-surface);

    &:not(.fullscreen) {
      @include for-size(tablet-portrait-up) {
        max-width: 50%;
        margin: 30px;
      }

      border-radius: 10px;

      .content {
        max-height: calc(100vh - 112px);
        overflow: auto;
      }
    }

    &.fullscreen {
      width: 100%;
      height: 100%;
      transform: translateY(100vh);

      .content {
        overflow: auto;
        position: absolute;
        top: 50px;
        left: 0;
        right: 0;
        height: calc(100vh - 50px);
      }
    }

    padding: 0px;
    z-index: 1;
    opacity: 0;
    transform: scale(0.85);
    transition: opacity 200ms cubic-bezier(.4, 0, 1, 1), transform 200ms cubic-bezier(.4, 0, 1, 1);

    .title {
      display: flex;
      justify-content: space-between;
      padding: 15px;
      font-weight: $bold;

      .btn-close {
        cursor: pointer;
        height: 20px;
        width: 20px;
        margin-left: 15px;
        fill: var(--secondary);
      }
    }

    .content {
      overflow-y: auto;
      overscroll-behavior: none;

      &:not(.dense) {
        padding: 0 15px 15px 15px;
      }
      height: 100%;
    }
  }

  .scrim {
    background-color: var(--scrim);
    transition: opacity 200ms cubic-bezier(.4, 0, 1, 1);
    opacity: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    backdrop-filter: blur(8px) grayscale(0.8);
  }

  &.dialog--open {
    .surface {
      transition: opacity 250ms cubic-bezier(0, 0, .2, 1), transform 250ms cubic-bezier(0, 0, .2, 1);
      opacity: 1;
      transform: scale(1);
      pointer-events: all;

      &.fullscreen {
        transform: translateY(0);
      }
    }

    .scrim {
      transition: opacity 250ms cubic-bezier(0, 0, .2, 1);
      opacity: 1;
      z-index: -1;
      pointer-events: all;
    }
  }
}
</style>
